import type { StatusTone } from '../abf/components/StatusChip';
import type { TemplateEntry, TemplateJson, TemplateStatus, VersionView } from './documentTemplatesApi';

export const TEMPLATES_PATH = '/document-templates';
export const versionPath = (id: number) => `${TEMPLATES_PATH}/${id}`;

/** Every state in words, with one tone; an unknown state falls back to neutral rather than throwing. */
const STATES: Record<TemplateStatus, { label: string; tone: StatusTone }> = {
  DRAFT: { label: 'Draft', tone: 'progress' },
  PROPOSED: { label: 'Awaiting a second person', tone: 'warning' },
  ACTIVE: { label: 'In use', tone: 'done' },
  RETIRED: { label: 'Retired', tone: 'neutral' },
};
export const stateOf = (status: string | null | undefined) =>
  STATES[(status || '') as TemplateStatus] || { label: status || 'Unknown', tone: 'neutral' as StatusTone };

export const FIELD_TYPES: Array<{ value: string; label: string }> = [
  { value: 'string', label: 'Text (short)' },
  { value: 'text', label: 'Text (long)' },
  { value: 'money', label: 'Amount' },
  { value: 'percent', label: 'Percent' },
  { value: 'bps', label: 'Basis points' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Whole number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'currency', label: 'Currency code' },
  { value: 'enum', label: 'One of a list' },
  { value: 'structure', label: 'Structured value (as instructed)' },
];
export const typeWord = (type: string | undefined) => FIELD_TYPES.find(t => t.value === type)?.label || type || '';

export const CITATION_WORDS: Record<string, string> = {
  REQUIRED: 'Must cite the page',
  OPTIONAL: 'Cite when found',
  NONE: 'No citation',
};

export type EntrySection = 'fields' | 'arrays' | 'objects';

export interface EntryRow {
  section: EntrySection;
  index: number;
  entry: TemplateEntry;
}

/** Template entries grouped by review group, in the template's group order. */
export const groupedEntries = (template: TemplateJson): Array<{ group: string; rows: EntryRow[] }> => {
  const order = [...(template.review_groups || [])];
  const groups = new Map<string, EntryRow[]>();
  (['fields', 'arrays', 'objects'] as EntrySection[]).forEach(section => {
    (template[section] || []).forEach((entry, index) => {
      const group = entry.review_group || 'Other';
      if (!order.includes(group)) order.push(group);
      groups.set(group, [...(groups.get(group) || []), { section, index, entry }]);
    });
  });
  return order.filter(group => groups.has(group)).map(group => ({ group, rows: groups.get(group) || [] }));
};

export const entryKind = (section: EntrySection, entry: TemplateEntry): string => {
  if (section === 'fields') return typeWord(entry.type);
  if (section === 'objects') return `Group of ${entry.item_fields?.length || 0} fields`;
  if (entry.item_type) return `List of ${typeWord(entry.item_type).toLowerCase()}`;
  return `Rows of ${entry.item_fields?.length || 0} fields`;
};

export const documentWord = (documentType: string) =>
  documentType.toLowerCase().split('_').map(word => (word === 'abf' ? 'ABF' : word[0].toUpperCase() + word.slice(1))).join(' ');

/** The newest version per document type of each state, for the list page. */
export const byType = (views: VersionView[]) => {
  const out = new Map<string, { active?: VersionView; open?: VersionView; versions: VersionView[] }>();
  views.forEach(view => {
    const type = view.template.documentType;
    const row = out.get(type) || { versions: [] };
    row.versions.push(view);
    if (view.template.status === 'ACTIVE') row.active = view;
    if (view.template.status === 'DRAFT' || view.template.status === 'PROPOSED') row.open = view;
    out.set(type, row);
  });
  return out;
};

export const when = (iso: string | null | undefined) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const showValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Not stated';
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return `${value.length} ${value.length === 1 ? 'row' : 'rows'}`;
  if (typeof value === 'object') return `${Object.keys(value as object).length} values`;
  return String(value);
};

/** A path a new field may take: one segment of lower-case letters, digits and underscores. */
export const pathFromName = (name: string) => '/' + name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
